import _ from 'lodash';
import Immutable from 'seamless-immutable';

import RefData from 'ref-data';
import { objectToParamsString } from '../../common/tools';
import { generatedWebEmbedCode, getWebEmbedParams } from '../../common/utils/publishing-utils';
import { Channel, DisplayContainer, DisplayUnit, SmartUrl } from '..';

/**
 * Publishing Destination
 * @typedef {object} PublishingDestination
 * @property {string} assetRef - the assetRef for the publishing destination
 * @property {object} channel - the channel object on which the publishing is displayed
 * @property {object} displayContainer - a deserialized displayContainer FE object (check the display-container-model for more details)
 * @property {object} displayUnit - a deserialized displayUnit FE object (check the display-unit-model for more details)
 * @property {string} icon - url for the publishing destintation
 * @property {boolean} isAdEmbed - is this an ad embed
 * @property {boolean} isResponsive - is it responsive
 * @property {boolean} isSocialStory - is this a social story embed
 * @property {boolean} isWebEmbed - is this a web embed
 * @property {boolean} isWebOverlay - is this a web overlay
 * @property {boolean} isRemoving - indicates whether the publishing destination is being removed
 * @property {string} name - the publishing destination name
 * @property {object} smartUrl - the shortened smart url to the campaign hosting the publishing destination
 * @property {string} type - human readable string describing the format type.
 */

function create(publishingDestination) {
  const result = _.clone(publishingDestination);

  // Assumption, all calls to create will already have channel, displayContainer, displayUnit, smartUrl hydrated into models
  result.isRemoving = publishingDestination.isRemoving || false;
  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend publishingDestination object into a frontend PublishingDestination Model
 * @param {Object} publishingDestination - backend formatted publishing destination
 * @returns {PublishingDestination}
 */
function deserialize(publishingDestination) {
  const result = _.pick(publishingDestination, _keys);
  result.channel = Channel.Model.deserialize(publishingDestination.channel);
  result.displayContainer = DisplayContainer.Model.deserialize(publishingDestination.displayContainer);
  result.displayUnit = DisplayUnit.Model.deserialize(publishingDestination.displayUnit);
  result.smartUrl = SmartUrl.Model.deserialize(publishingDestination.smartUrl);
  return create(result);
}

function serialize(publishingDestination) {
  delete publishingDestination.icon;
  return publishingDestination;
}

const model = {
  get embedCodeParams() {
    return {
      mode: this.displayUnit.embedType,
      ...(this.displayContainer.growable &&
        this.displayContainer.hasDimensions && {
        ratio: `${this.displayContainer.xDimension}:${this.displayContainer.yDimension}`,
      }),
      ...(!this.displayContainer.growable &&
        this.displayContainer.xDimension && { width: this.displayContainer.xDimension }),
      ...(!this.displayContainer.growable &&
        this.displayContainer.yDimension && { height: this.displayContainer.yDimension }),
    };
  },
  get assetRef() {
    return this.displayUnit.assetRef;
  },
  get id() {
    return JSON.stringify({
      channelId:          this.channel.id,
      displayContainerId: this.displayContainer.id,
      displayUnitId:      this.displayUnit.id,
      smartUrlId:         this.smartUrl.id,
    });
  },
  get isPublished() {
    return this.displayContainer.isPublished;
  },
  get isResponsive() {
    return this.displayContainer.isResponsive;
  },
  get isSocialStory() {
    return this.displayContainer.typeRef.destinationType === RefData.Enum.TYPE.DESTINATION.STORY;
  },
  get isMicrosite() {
    return this.displayContainer.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.MICROSITE;
  },
  get isWebEmbed() {
    return this.displayContainer.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.EMBED;
  },
  get isWebOverlay() {
    return this.displayContainer.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.OVERLAY;
  },
  get name() {
    if (this.displayContainer.hasDimensions) {
      return this.displayContainer.isResponsive
        ? `${this.displayContainer.xDimension}:${this.displayContainer.yDimension}`
        : `${this.displayContainer.xDimension}x${this.displayContainer.yDimension}`;
    } else {
      return this.displayContainer.isResponsive ? 'W:H' : 'WxH';
    }
  },
  get publishedBy() {
    return this.displayContainer.createdBy;
  },
  get script() {
    return this.displayContainer.script;
  },
  get status() {
    return this.displayContainer.status;
  },
  get type() {
    switch (this.displayContainer.typeRef.id) {
      case RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.EMBED:
        return 'Web Embed';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.OVERLAY:
        return 'Lightbox';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.MICROSITE:
        return 'Microsite';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_FACEBOOK.STORY:
        return 'Facebook Story';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_SNAPCHAT.STORY:
        return 'Snapchat Story';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_TWITTER.STORY:
        return 'X Story';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_TWITTER.INNETWORK:
        return 'X';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_INSTAGRAM.STORY:
        return 'Instagram Story';
      case RefData.Enum.TYPE.DISPLAYCONTAINER_INSTAGRAM.INNETWORK:
        return 'Instagram';
      default:
        return '';
    }
  },
  get url() {
    return this.smartUrl.url;
  },
};

/** When in page mode, if you have a responsive web embed, it will not respect the ratio.
 * Instead, width will be 100% and height will be determined by the content height.
 */
function ignoresDimensions({ displayUnit, experience }) {
  return !!(displayUnit.isWebUnit && displayUnit.isResponsive && experience.isPageMode);
}

function generatedAdvancedEmbedCode(publishingDestination, isMicroExperience, isOpenId) {
  const { growable, xDimension, yDimension, displayConfig } = publishingDestination.displayContainer;
  const params = getWebEmbedParams(publishingDestination, isMicroExperience);

  if (!isMicroExperience && growable) {
    params.mode = 'responsive';
  }

  let embedCode = publishingDestination.displayContainer[isOpenId ? 'iframeTemplateOpenId' : 'iframeTemplate'].replace(
    /{params}/g,
    objectToParamsString(params)
  );

  const scrolling = displayConfig.webEmbed.scrollBar ? 'yes' : 'no';
  embedCode = embedCode
    .replace(/scrolling=("no"|"yes")/g, `scrolling="${scrolling}"`) // replace iframe props
    .replace(/scrolling=(no|yes)/g, `scrolling=${scrolling}`); // replace url query params

  if (!publishingDestination.isResponsive || !isMicroExperience) {
    embedCode = embedCode.replace(/{width}/g, xDimension).replace(/{height}/g, yDimension);
  } else {
    embedCode = embedCode.replace(/{width}/g, '100%').replace(/{height}/g, '600');
  }

  return embedCode;
}

/**
 * Returns the simple embed code for the publishing destination
 */
function embedCode(publishingDestination, isMicroExperience, isOpenId) {
  return generatedWebEmbedCode(publishingDestination, isMicroExperience, isOpenId);
}

function publishingHeader(publishingDestination) {
  if (publishingDestination.isResponsive) {
    return 'Dynamic';
  } else if (!publishingDestination.isMicrosite) {
    return publishingDestination.name;
  }
  return '';
}

const _keys = ['channel', 'displayContainer', 'displayUnit', 'smartUrl'];

export default {
  create,
  deserialize,
  ignoresDimensions,
  publishingHeader,
  embedCode,
  generatedAdvancedEmbedCode,
  serialize,
};
